/**
 * Centralized subdomain resolution utility
 * Single source of truth for determining the current organization subdomain
 */

export const resolveSubdomain = (): string => {
  if (typeof window === 'undefined') return 'swift';
  
  const hostname = window.location.hostname;
  
  // Strategy 1: Production subdomain extraction (check FIRST for security)
  if (isProductionEnvironment(hostname)) {
    const parts = hostname.split('.');
    if (parts.length >= 3) {
      const subdomain = parts[0];
      console.log('[SUBDOMAIN] Resolved from hostname:', subdomain);
      return subdomain;
    }
  }
  
  // Strategy 2: URL parameter override (development/testing only)
  const urlParams = new URLSearchParams(window.location.search);
  const orgParam = urlParams.get('org');
  if (orgParam) {
    console.log('[SUBDOMAIN] Resolved from URL param:', orgParam);
    return orgParam;
  }
  
  // Strategy 3: localStorage preference (development helper only)
  const devOrg = localStorage.getItem('dev-organization');
  if (devOrg) {
    console.log('[SUBDOMAIN] Resolved from localStorage:', devOrg);
    return devOrg;
  }
  
  // Fallback: Default organization
  console.log('[SUBDOMAIN] Using default: swift');
  return 'swift';
};

/**
 * Check if we're in a production environment that supports subdomains
 */
const isProductionEnvironment = (hostname: string): boolean => {
  return !hostname.includes('localhost') && 
         !hostname.includes('127.0.0.1') && 
         !hostname.includes('.lovableproject.com') &&
         !hostname.includes('.sandbox.lovable.dev') &&
         !hostname.includes('vercel.app');
};

/**
 * Get cached theme for an organization
 */
export const getCachedTheme = (orgId: string): Record<string, string> | null => {
  try {
    const cached = localStorage.getItem(`theme-cache-${orgId}`);
    if (cached) {
      const data = JSON.parse(cached);
      // Check if cache is less than 24 hours old
      if (Date.now() - data.timestamp < 24 * 60 * 60 * 1000) {
        return data.theme;
      }
    }
  } catch (error) {
    console.error('Error reading theme cache:', error);
  }
  return null;
};

/**
 * Cache theme for an organization
 */
export const cacheTheme = (orgId: string, theme: Record<string, string>): void => {
  try {
    localStorage.setItem(`theme-cache-${orgId}`, JSON.stringify({
      theme,
      timestamp: Date.now()
    }));
  } catch (error) {
    console.error('Error caching theme:', error);
  }
};

/**
 * Clear theme cache for an organization
 * NOTE: Does NOT clear org-id cache which is needed for instant theme loading
 */
export const clearThemeCache = (orgId?: string): void => {
  try {
    if (orgId) {
      localStorage.removeItem(`theme-cache-${orgId}`);
      console.log('[THEME-CACHE] Cleared cache for org:', orgId);
    } else {
      // Clear all theme caches but preserve org-id entries
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.startsWith('theme-cache-')) {
          localStorage.removeItem(key);
        }
        // DO NOT remove org-id-${subdomain} entries - needed for instant loading
      });
      console.log('[THEME-CACHE] Cleared all theme caches');
    }
  } catch (error) {
    console.error('Error clearing theme cache:', error);
  }
};

/**
 * Reset all CSS custom properties to defaults
 */
export const resetCssVariables = (): void => {
  const root = document.documentElement;
  const variablesToReset = [
    '--primary',
    '--secondary',
    '--accent',
    '--warning',
    '--destructive',
    '--radius',
    '--font-scale'
  ];
  
  variablesToReset.forEach(variable => {
    root.style.removeProperty(variable);
  });
};
